import React from 'react';
import { Workflow } from '../../../types/workflow';

function rotationStatus(daysAgo: number, interval: number) {
  if (daysAgo < interval * 0.66) return { color: '#0F6E56', label: 'Fresh' };
  if (daysAgo < interval)         return { color: '#92400e', label: 'Aging' };
  return { color: '#b91c1c', label: 'Overdue' };
}

export function SecurityTab({ workflow }: { workflow: Workflow }) {
  const now = Date.now();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <section className="bg-white border border-trim rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-trim">
          <h3 className="text-sm font-semibold text-ink">RBAC roles</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-trim bg-wash">
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Role</th>
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Permissions</th>
              <th className="text-right px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Members</th>
            </tr>
          </thead>
          <tbody>
            {workflow.security.rbac_roles.map(r => (
              <tr key={r.role} className="border-b border-wash last:border-0">
                <td className="px-5 py-2.5 text-ink font-medium text-sm">{r.role}</td>
                <td className="px-5 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {r.permissions.map(p => (
                      <span key={p} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-wash text-copy">{p}</span>
                    ))}
                  </div>
                </td>
                <td className="px-5 py-2.5 text-right font-mono text-xs">{r.member_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="bg-white border border-trim rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-trim">
          <h3 className="text-sm font-semibold text-ink">Secrets</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-trim bg-wash">
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Name</th>
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Value</th>
              <th className="text-right px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Last rotated</th>
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Status</th>
            </tr>
          </thead>
          <tbody>
            {workflow.security.secrets.map(s => {
              const daysAgo = (now - new Date(s.last_rotated).getTime()) / (1000 * 60 * 60 * 24);
              const st = rotationStatus(daysAgo, s.rotation_interval_days);
              return (
                <tr key={s.name} className="border-b border-wash last:border-0">
                  <td className="px-5 py-2.5 font-mono text-xs text-ink">{s.name}</td>
                  <td className="px-5 py-2.5 font-mono text-xs text-copy">{s.masked_value}</td>
                  <td className="px-5 py-2.5 text-right text-xs text-muted">{Math.round(daysAgo)}d ago</td>
                  <td className="px-5 py-2.5 text-xs font-medium" style={{ color: st.color }}>{st.label}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="bg-white border border-trim rounded-xl overflow-hidden lg:col-span-2">
        <div className="px-5 py-3 border-b border-trim">
          <h3 className="text-sm font-semibold text-ink">PII classification</h3>
        </div>
        <ul className="px-5 py-3 space-y-2">
          {workflow.security.pii_classification.map((p, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <span className="font-mono text-xs text-ink">{p.field}</span>
              <span className="text-copy">{p.classification}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
